import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download, Landmark, AlertTriangle, CreditCard, CheckCircle2, FileSpreadsheet, Briefcase } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseMonthYear(dateStr) {
  if (!dateStr || dateStr.length < 7) return null;
  const [y, m] = dateStr.split('-');
  return { month: parseInt(m), year: parseInt(y), label: `${MONTH_NAMES[parseInt(m) - 1]}-${y.slice(2)}` };
}

function getFY(month, year) {
  const fyStart = month >= 4 ? year : year - 1;
  return `FY ${fyStart}-${String(fyStart + 1).slice(2)}`;
}

function getAvailableFYs(stmts) {
  const fys = new Set();
  stmts.forEach(s => { if (s.parsed) fys.add(getFY(s.parsed.month, s.parsed.year)); });
  return [...fys].sort().reverse();
}

function filterByFY(stmts, fy) {
  if (!fy || fy === 'all') return stmts;
  return stmts.filter(s => s.parsed && getFY(s.parsed.month, s.parsed.year) === fy);
}

const START_YEAR = 2025;
const START_MONTH = 4;

function buildMonthGrid(account) {
  const stmts = account.statements
    .map(s => ({ ...s, parsed: parseMonthYear(s.statement_date) }))
    .filter(s => s.parsed);

  // Statement-date based: direct PDF per month
  const stmtByMonth = {};
  stmts.forEach(s => {
    const key = `${s.parsed.year}-${String(s.parsed.month).padStart(2, '0')}`;
    stmtByMonth[key] = s;
  });

  // Transaction-covered months (from backend) — no individual PDF but data exists
  const coveredByTxn = {};
  (account.covered_months || []).forEach(cm => {
    if (typeof cm === 'object') {
      coveredByTxn[cm.month] = cm.parent_id;
    } else {
      coveredByTxn[cm] = account.parent_statement_id;
    }
  });

  // Derive lifecycle from actual statements when available — registered card
  // metadata may be narrower than the real statement coverage
  let stmtEarliest = null, stmtLatest = null;
  if (stmts.length > 0) {
    stmtEarliest = stmts.reduce((min, s) => {
      const k = `${s.parsed.year}-${String(s.parsed.month).padStart(2, '0')}`;
      return !min || k < min ? k : min;
    }, null);
    stmtLatest = stmts.reduce((max, s) => {
      const k = `${s.parsed.year}-${String(s.parsed.month).padStart(2, '0')}`;
      return !max || k > max ? k : max;
    }, null);
  }
  let activeFrom = account.active_from || '';
  // Expand activeFrom to cover earlier statements (don't hide existing data)
  if (stmtEarliest && (!activeFrom || stmtEarliest < activeFrom)) activeFrom = stmtEarliest;
  // Respect backend activeUntil as closure date — do NOT expand it
  const activeUntil = account.active_until || '';

  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const months = [];
  let y = START_YEAR, m = START_MONTH;
  while (`${y}-${String(m).padStart(2, '0')}` <= currentKey) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    const label = `${MONTH_NAMES[m - 1]}-${String(y).slice(2)}`;
    // Filter by lifecycle — skip months before start and after closure
    const inLifecycle = (!activeFrom || key >= activeFrom) && (!activeUntil || key <= activeUntil);
    const isCurrentMonth = key === currentKey;
    const hasStatement = !!stmtByMonth[key];
    const hasCoverage = !!coveredByTxn[key];
    if (inLifecycle && (!isCurrentMonth || hasStatement || hasCoverage)) {
      if (stmtByMonth[key]) {
        months.push({ ...stmtByMonth[key], type: 'available', monthKey: key });
      } else if (coveredByTxn[key]) {
        months.push({
          parsed: { month: m, year: y, label },
          type: 'covered',
          monthKey: key,
          parentId: coveredByTxn[key],
        });
      } else {
        months.push({ parsed: { month: m, year: y, label }, type: 'missing', monthKey: key });
      }
    }
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months.reverse();
}

function AccountTable({ accounts, isBankTab, downloadPdf, downloadMonthlyExtract, downloadByFY, isClosed }) {
  return (
    <div className={`bg-white border rounded-md overflow-hidden ${isClosed ? 'border-[#E2E0D8]/70 opacity-80' : 'border-[#E2E0D8]'}`}>
      <table className="w-full">
        <thead>
          <tr className="bg-[#F7F6F2] border-b border-[#E2E0D8]">
            <th className="text-left px-5 py-3 text-xs font-semibold text-[#5C5C55] uppercase tracking-wider">Statement Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Previous Months</th>
            <th className="px-3 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account, ai) => {
            const stmts = account.statements
              .map(s => ({ ...s, parsed: parseMonthYear(s.statement_date) }))
              .filter(s => s.parsed);
            const availableFYs = getAvailableFYs(stmts);
            const allMonths = buildMonthGrid(account);
            const missingCount = allMonths.filter(m => m.type === 'missing').length;
            const rows = [];
            for (let i = 0; i < allMonths.length; i += 6) rows.push(allMonths.slice(i, i + 6));
            if (rows.length === 0) return null;
            return rows.map((row, ri) => (
              <tr key={`${account.name}-${ri}`}
                className={`border-b border-[#E2E0D8] ${ai % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'}`}>
                <td className="px-5 py-2.5 align-top whitespace-nowrap">
                  {ri === 0 ? (
                    <div>
                      <div className="flex items-center gap-2">
                        {isBankTab ? <Landmark size={14} className="text-[#284A33] shrink-0" /> : <CreditCard size={14} className="text-[#284A33] shrink-0" />}
                        <span className="text-sm font-medium text-[#1C1C19]">{account.name}</span>
                        {isClosed && account.active_until && (
                          <span className="text-[10px] text-[#999] bg-[#F0F0EC] px-1.5 py-0.5 rounded">
                            Closed {account.active_until}
                          </span>
                        )}
                      </div>
                      {missingCount > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <AlertTriangle size={11} className="text-amber-500" />
                          <span className="text-[10px] text-amber-600">{missingCount} missing</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </td>
                {row.map((item, ci) => (
                  <td key={ci} className="px-4 py-2.5">
                    {item.type === 'available' ? (
                      <button
                        onClick={() => downloadPdf(item.id, item.display_name || item.original_filename)}
                        data-testid={`dl-${item.monthKey}`}
                        className="text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8] hover:underline cursor-pointer transition-colors">
                        {item.parsed.label}
                      </button>
                    ) : item.type === 'covered' ? (
                      <button
                        onClick={() => item.parentId && downloadMonthlyExtract(item.parentId, item.monthKey)}
                        data-testid={`dl-covered-${item.monthKey}`}
                        title="Monthly extract from FY statement — click to download CSV"
                        className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline cursor-pointer transition-colors">
                        <CheckCircle2 size={12} />
                        {item.parsed.label}
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded border border-red-200"
                        data-testid={`dl-missing-${item.monthKey}`}
                        title="Statement missing for this month">
                        {item.parsed.label}
                      </span>
                    )}
                  </td>
                ))}
                {Array.from({ length: 6 - row.length }).map((_, i) => (
                  <td key={`empty-${i}`} className="px-4 py-2.5"></td>
                ))}
                <td className="px-3 py-2.5 text-right align-top">
                  {ri === 0 && (
                    <Select onValueChange={(fy) => downloadByFY(account, fy)}>
                      <SelectTrigger data-testid={`download-fy-${account.name}`}
                        className="h-7 w-[130px] text-xs border-[#284A33]/30 text-[#284A33]">
                        <Download size={12} className="mr-1" />
                        <SelectValue placeholder="Download" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        {availableFYs.map(fy => (
                          <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClaimsPanel() {
  const [claimsFY, setClaimsFY] = useState('FY 2025-26');
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const MONTH_LABELS = ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar'];

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await axios.get(`${API}/transactions/claims-summary`, {
        params: { fy: claimsFY }, withCredentials: true,
      });
      setSummary(data);
    } catch { setSummary(null); }
    setLoadingSummary(false);
  }, [claimsFY]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const downloadMonth = async (monthKey) => {
    try {
      const response = await axios.get(`${API}/transactions/export-monthly`, {
        params: { fy: claimsFY, month: monthKey }, withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Claims_${monthKey}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${monthKey}`);
    } catch { toast.error('Download failed'); }
  };

  const downloadClaims = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/transactions/export-monthly`, {
        params: { fy: claimsFY }, withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Claims_${claimsFY.replace(' ', '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Claims Excel downloaded');
    } catch { toast.error('Download failed'); }
    setDownloading(false);
  };

  const months = summary?.months || [];
  const grandTotal = months.reduce((s, m) => s + (m.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="claims-panel">
      <div className="bg-white border border-[#E2E0D8] rounded-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8]">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#284A33]/10"><FileSpreadsheet size={18} className="text-[#284A33]" /></div>
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1C1C19]">Business Claims</h3>
              <p className="text-[11px] text-[#5C5C55] mt-0.5">Monthly business expenses from personal accounts — Excel with separate tabs per month</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={claimsFY} onValueChange={setClaimsFY}>
              <SelectTrigger className="w-[130px] h-8 text-xs border-[#E2E0D8]" data-testid="claims-fy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FY 2025-26">FY 2025-26</SelectItem>
                <SelectItem value="FY 2026-27">FY 2026-27</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={downloadClaims} disabled={downloading} data-testid="claims-download-btn"
              className="bg-[#284A33] text-white hover:bg-[#1C3624] h-8 px-4 text-xs">
              <Download size={13} className="mr-1.5" />{downloading ? 'Generating...' : 'Download Excel'}
            </Button>
          </div>
        </div>

        {/* Monthly Summary Table */}
        {loadingSummary ? (
          <div className="p-8 text-center text-sm text-[#5C5C55]">Loading summary...</div>
        ) : months.length === 0 ? (
          <div className="p-10 text-center">
            <Briefcase size={28} className="mx-auto mb-2 text-[#D5D2C6]" />
            <p className="text-sm text-[#5C5C55]">No business claims found for {claimsFY}</p>
          </div>
        ) : (
          <table className="w-full" data-testid="claims-summary-table">
            <thead>
              <tr className="bg-[#F7F6F2]/60 border-b border-[#E2E0D8]">
                <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#5C5C55] font-semibold">Month</th>
                <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#5C5C55] font-semibold">Entries</th>
                <th className="text-right px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#5C5C55] font-semibold">Amount</th>
                <th className="w-10 px-2 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={m.month} className={`border-b border-[#E2E0D8]/60 ${m.count > 0 ? 'hover:bg-[#F7F6F2]/40 cursor-pointer' : 'opacity-50'}`}
                  onClick={() => m.count > 0 && downloadMonth(m.month)}
                  data-testid={`claims-month-${m.month}`}>
                  <td className="px-5 py-2.5">
                    <span className={`text-sm font-medium ${m.count > 0 ? 'text-[#2563EB] hover:underline' : 'text-[#1C1C19]'}`}>{MONTH_LABELS[i]} {m.month.split('-')[0]}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-xs font-mono ${m.count > 0 ? 'text-[#1C1C19]' : 'text-[#D5D2C6]'}`}>{m.count || '—'}</span>
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <span className={`text-sm font-mono ${m.count > 0 ? 'text-[#1C1C19]' : 'text-[#D5D2C6]'}`}>
                      {m.count > 0 ? `₹${m.total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {m.count > 0 && <Download size={13} className="text-[#5C5C55] hover:text-[#284A33] transition-colors inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#F7F6F2]/80 border-t-2 border-[#1C1C19]/15">
                <td className="px-5 py-3"><span className="text-sm font-semibold text-[#1C1C19]">Total</span></td>
                <td className="px-4 py-3 text-center"><span className="text-xs font-mono font-semibold text-[#1C1C19]">{summary?.total_count || 0}</span></td>
                <td className="px-5 py-3 text-right">
                  <span className="text-sm font-mono font-semibold text-[#1C1C19]">
                    ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

export default function DownloadsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('bank_account');

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/downloads/by-account`, { withCredentials: true });
      setAccounts(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadPdf = async (stmtId, displayName) => {
    try {
      const response = await axios.get(`${API}/statements/${stmtId}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      let downloadName = displayName;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) downloadName = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Download failed'); }
  };

  const downloadMonthlyExtract = async (stmtId, month) => {
    try {
      const response = await axios.get(`${API}/downloads/monthly-extract/${stmtId}`, {
        withCredentials: true, responseType: 'blob', params: { month }
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${month}_extract.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('No data for this month'); }
  };

  const downloadByFY = async (account, fy) => {
    const stmts = account.statements.map(s => ({ ...s, parsed: parseMonthYear(s.statement_date) })).filter(s => s.parsed);
    const filtered = filterByFY(stmts, fy);
    if (filtered.length === 0) { toast.info('No statements for this FY'); return; }
    for (const s of filtered) {
      await downloadPdf(s.id, s.display_name || s.original_filename);
      await new Promise(r => setTimeout(r, 500));
    }
    toast.success(`Downloaded ${filtered.length} statements for ${fy}`);
  };

  if (loading) return <div className="p-8 text-center text-[#5C5C55]">Loading...</div>;

  const filteredAccounts = accounts.filter(a => a.type === tab);
  const isBankTab = tab === 'bank_account';

  // Segregate active vs closed accounts
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const activeAccounts = filteredAccounts.filter(a => !a.active_until);
  const closedAccounts = filteredAccounts.filter(a => !!a.active_until);

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1200px]" data-testid="downloads-page">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Downloads</h2>
        <p className="text-sm text-[#5C5C55] mt-1">Download password-free copies of your statements. Click on the month to download.</p>
      </div>

      <div className="flex gap-1 bg-[#E8E6DE] p-1 rounded-lg w-fit">
        <button data-testid="dl-tab-bank"
          onClick={() => setTab('bank_account')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'bank_account' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <Landmark size={15} /> Bank Accounts
        </button>
        <button data-testid="dl-tab-cc"
          onClick={() => setTab('credit_card')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'credit_card' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <CreditCard size={15} /> Credit Cards
        </button>
        <button data-testid="dl-tab-claims"
          onClick={() => setTab('claims')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'claims' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <Briefcase size={15} /> Claims
        </button>
      </div>

      {tab === 'claims' ? (
        <ClaimsPanel />
      ) : (
        <>
          {/* Legend */}
          <div className="flex gap-4 text-xs text-[#5C5C55]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563EB]" /> Monthly PDF</span>
            <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" /> Monthly extract from FY statement</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-300" /> Missing</span>
          </div>

          {filteredAccounts.length === 0 ? (
            <div className="bg-white border border-[#E2E0D8] rounded-md p-12 text-center">
              {isBankTab ? <Landmark className="mx-auto mb-3 text-[#D5D2C6]" size={40} /> : <CreditCard className="mx-auto mb-3 text-[#D5D2C6]" size={40} />}
              <p className="text-[#5C5C55]">No {isBankTab ? 'bank' : 'credit card'} statements uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {activeAccounts.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Active {isBankTab ? 'Bank Accounts' : 'Credit Cards'}
                  </h3>
                  <AccountTable accounts={activeAccounts} isBankTab={isBankTab} downloadPdf={downloadPdf} downloadMonthlyExtract={downloadMonthlyExtract} downloadByFY={downloadByFY} />
                </div>
              )}
              {closedAccounts.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#999] mb-2 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#CCC]" />
                    Closed {isBankTab ? 'Bank Accounts' : 'Credit Cards'}
                  </h3>
                  <AccountTable accounts={closedAccounts} isBankTab={isBankTab} downloadPdf={downloadPdf} downloadMonthlyExtract={downloadMonthlyExtract} downloadByFY={downloadByFY} isClosed />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
